// The app's single icon set, drawn in the same "pen" as the Peak mermaid logo:
// single stroke, rounded caps/joins, no fill. Icons inherit color via
// `currentColor`, so the surrounding control's text color drives the icon color
// (active nav links go brand-green, a destructive menu row goes red).
//
// House rules — follow them when adding a glyph so the set stays one family:
//   • 24×24 grid, terminals on .5 coordinates so a 16px render stays crisp
//   • stroke-width 1.7, round cap, round join, no fill, no two-tone — the ONLY
//     exception is the status glyphs' watercolour wash, see WASH below
//   • glyphs fill ~18 of the 24 units (circular marks a touch smaller, since a
//     circle reads larger than a square at equal size)
//   • never hard-code a color — always `currentColor`
//   • directional glyphs pass `flip` at the call site to mirror in RTL; a
//     mirrored trash can is just a wrong trash can, so non-directional ones don't
//   • no currency symbols: the app is going multi-currency, so money glyphs stay
//     neutral (`coins`, `cash`, `card`) and currency lives in the amount
//
// `restaurant` is a dining fork — it echoes the fork the mermaid holds and marks
// the active-restaurant context, distinct from `suppliers` (a delivery box).
const PATHS = {
  // ── navigation & structure
  suppliers:  'M3 7.5l9-4 9 4-9 4-9-4z M3 7.5v9l9 4 9-4v-9 M12 11.5v9',
  documents:  'M6.5 3.5h7l4 4v13h-11z M13.5 3.5v4h4 M9 12.5h6 M9 16h6',
  // Credits — a banknote with value arriving back in it. Deliberately not a
  // u-turn arrow: a זיכוי is money the restaurant is owed and has to claim,
  // not merely a "return".
  credits:    'M3.5 11.5h17v9h-17z M12 19a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5 M12 8.5V3 M8.8 5.6 12 8.8l3.2-3.2',
  analysis:   'M3.5 20.5h17 M6.5 20.5V12 M11.5 20.5V5.5 M16.5 20.5v-6',
  // Management/admin — an equalizer-sliders glyph (two rails with knobs),
  // reads as "controls" (deliberately not a gear/sun).
  admin:      'M8 4v5 M8 13v7 M6 11h4 M16 4v9 M16 17v3 M14 15h4',
  restaurant: 'M8.5 3.5V9 M12 3.5V9 M15.5 3.5V9 M8.5 9c0 2.2 3.5 2 3.5 4 M15.5 9c0 2.2-3.5 2-3.5 4 M12 13v7.5',
  // עץ מוצר — a broadleaf tree, for the dish-costing module. The name means
  // "product tree", and the module really is one: a dish at the top, the
  // catalog products it is built from underneath.
  //
  // Deliberately NOT the `restaurant` fork it borrowed at first: that glyph
  // marks the ACTIVE-RESTAURANT context in the tenant chip, so the nav link and
  // the tenant switcher were wearing the same mark for two unrelated things.
  // Also not a conifer, which drew legibly but reads as a Christmas tree — a
  // rounded, lobed crown sits closer to the mermaid's organic line.
  tree:       'M5.6 12a4 4 0 0 1 2.2-6.9 4.4 4.4 0 0 1 8.4 0 4 4 0 0 1 2.2 6.9 4 4 0 0 1-5 1.4H10.6a4 4 0 0 1-5-1.4z M12 20.5v-7',
  menu:       'M4 7h16 M4 12h16 M4 17h16',
  close:      'M6 6l12 12 M18 6L6 18',
  // Magnifier — also the lighter "no filter matches" empty-state mark.
  search:     'M10 4a6 6 0 1 0 0 12 6 6 0 1 0 0-12z M14.5 14.5L20 20',
  // Down chevron — dropdown/switcher carets (replaces the Unicode ▾).
  chevron:    'M6 9l6 6 6-6',
  // Back/forward chevron. Points LEFT, the forward direction in this RTL app;
  // pass `flip` for the backward direction. (A Unicode '‹' can't be used: it is
  // bidi-mirrored and flips to '›' in RTL.)
  'chevron-inline': 'M15 18L9 12l6-6',

  // ── objects
  receipt:    'M6 3.5h12v18l-3-2.2-3 2.2-3-2.2-3 2.2z M9.3 8.5h5.4 M9.3 12.3h5.4',
  docs:       'M8.5 3.5h6l3.5 3.5v10.5h-9.5z M14.5 3.5v3.5h3.5 M5.5 7v13.5h9',
  store:      'M3.5 8.5l1.8-4.5h13.4l1.8 4.5 M3.5 8.5a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 M5 10.5v10h14v-10 M9.5 20.5v-6h5v6',
  users:      'M16 20.5v-1.5a4 4 0 0 0-4-4h-4a4 4 0 0 0-4 4v1.5 M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7 M21 20.5v-1.5a4 4 0 0 0-3-3.87 M16.5 4.25a4 4 0 0 1 0 7.5',
  network:    'M12 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5 M5.5 15.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5 M18.5 15.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5 M10.4 8.9L7.1 15.2 M13.6 8.9l3.3 6.3 M8.2 18h7.6',
  tag:        'M20.4 13.2l-7.2 7.2a1.8 1.8 0 0 1-2.6 0L3.5 13.3V4h9.3l7.6 7.6a1.1 1.1 0 0 1 0 1.6z M7.8 8.3h.01',
  folder:     'M3.5 19.5v-14h5.4l2 2.6h9.6v11.4z',
  calendar:   'M4.5 5.5h15v15h-15z M4.5 10h15 M8.5 3.5v4 M15.5 3.5v4',
  // An envelope with the message flying out — "send an email", not "inbox".
  'mail-send': 'M8.5 6h12v12h-12 M20.5 6.6l-6 4.5-6-4.5 M1.8 12h6.2 M4.6 9.2 1.8 12l2.8 2.8',

  // ── money (currency-neutral by design — see the house rules above)
  card:       'M2.5 5.5h19v13h-19z M2.5 10h19 M6 14.5h3.5',
  bank:       'M3.5 9.5h17 M12 3.5l8.5 6h-17z M6.5 12v5.5 M10 12v5.5 M14 12v5.5 M17.5 12v5.5 M4 20.5h16',
  cash:       'M2.5 6.5h19v11h-19z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6 M6 10h.01 M18 14h.01',
  cheque:     'M3.5 5.5h17v13h-17z M6.5 9.5h5 M6.3 15c1.5-3 2.8 3 4.3 0s2.8 3 4.3 0',
  recurring:  'M20 5.5v5h-5 M4 18.5v-5h5 M19.1 9.5a7.5 7.5 0 0 0-13-3.1L4 8.5 M4.9 14.5a7.5 7.5 0 0 0 13 3.1l2.1-2.1',
  coins:      'M9.5 3.5a6 6 0 1 0 0 12 6 6 0 0 0 0-12z M14.5 8.5a6 6 0 1 0 0 12 6 6 0 0 0 0-12z',
  trend:      'M3.5 20.5h17 M6.5 15.5l4.2-4.2 3 3L20 8',

  // ── actions
  upload:     'M12 16.5V4 M7.5 8.5L12 4l4.5 4.5 M4.5 15v5.5h15V15',
  download:   'M12 4v12.5 M7.5 12L12 16.5 16.5 12 M4.5 15v5.5h15V15',
  // Excel/spreadsheet export — a sheet grid, no arrow bolted on.
  spreadsheet: 'M4 4h16v16H4z M4 9.5h16 M4 15h16 M9.5 9.5V20 M15 9.5V20',
  save:       'M4.5 4.5h11l4 4v11h-15z M8 4.5v5h6v-5 M8 19.5v-6h8v6',
  // A slim pencil read at a glance (tip, body, eraser band) — the card's
  // one-tap edit. `edit` above is the older square-ish glyph used elsewhere.
  pencil:     'M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4.5 1.5L5 15z M14 6l3 3 M5 15l3 3',
  edit:       'M4 20h4l11-11a2.12 2.12 0 0 0-3-3L5 17z M14.5 5.5l3 3',
  trash:      'M3.5 6.5h17 M8.5 6.5V4h7v2.5 M6 6.5l1 14h10l1-14 M10 10.5v6 M14 10.5v6',
  comment:    'M20.5 15.5a2 2 0 0 1-2 2h-11L3.5 21.5v-16a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z',
  clip:       'M21.4 11l-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5',
  eye:        'M1.8 12S5.6 4.8 12 4.8 22.2 12 22.2 12 18.4 19.2 12 19.2 1.8 12 1.8 12z M12 15.1a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2',
  // Lock — a restricted (בעלים/מטה בלבד) supplier and the no-access screen.
  // Deliberately not `eye-off`: the supplier is not merely out of view, access
  // to it is closed, and `eye-off` already means "hide password" in PasswordInput.
  lock:       'M5.5 10.5h13v10h-13z M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3 M12 14.5v2.5',
  'eye-off':  'M1.8 12S5.6 4.8 12 4.8 22.2 12 22.2 12 18.4 19.2 12 19.2 1.8 12 1.8 12z M12 15.1a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2 M3.5 20.5 20.5 3.5',
  link:       'M10.2 13.5a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7.1-7.1l-1.7 1.8 M13.8 10.5a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7.1 7.1l1.7-1.8',
  // A BROKEN link, for the state where a תעודת משלוח is NOT linked to its
  // מרכזת. Same two hooks as `link`, translated 1.1 apart along the chain's
  // own axis so the arcs stay byte-identical (relative commands: only the
  // opening M moves), plus two short ticks across the gap so the break is
  // legible at 13px — where a 2px gap alone would read as a rendering seam.
  unlink:     'M11.3 12.4a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7.1-7.1l-1.7 1.8 M12.7 11.6a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7.1 7.1l1.7-1.8 M9.4 9.4l1.5 1.5 M13.1 13.1l1.5 1.5',
  merge:      'M3.5 6h5.5a5 5 0 0 1 5 6 M3.5 18h5.5a5 5 0 0 0 5-6 M13.5 12h6.5 M17.6 9.5l2.5 2.5-2.5 2.5',
  plus:       'M12 5v14 M5 12h14',
  send:       'M21.5 2.5L10.8 13.2 M21.5 2.5l-6.7 19-3.9-8.4-8.4-3.9z',
  history:    'M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.4 M3.5 3.8v4.6h4.6 M12 7.6V12l3.2 1.9',
  list:       'M8.5 5.5h11.5 M8.5 12h11.5 M8.5 18.5h11.5 M4 5.5h.01 M4 12h.01 M4 18.5h.01',
  // Circular counter-clockwise "undo" — used for "return to open".
  undo:       'M1 4v6h6 M3.51 15a9 9 0 1 0 2.13-9.36L1 10',
  // AI extraction — a spark, not a robot.
  sparkle:    'M10 3.5l1.6 4.2 4.2 1.6-4.2 1.6L10 15.1 8.4 10.9 4.2 9.3l4.2-1.6z M17.8 14.4l.9 2.3 2.3.9-2.3.9-.9 2.3-.9-2.3-2.3-.9 2.3-.9z',
  flask:      'M9.5 3.5v6.2L4.9 18a2 2 0 0 0 1.7 3h10.8a2 2 0 0 0 1.7-3l-4.6-8.3V3.5 M8 3.5h8 M7.4 14.5h9.2',
  filter:     'M3.5 5h17l-6.8 8v6.5l-3.4-2v-4.5z',
  sort:       'M7 4.5v15 M4 7.5l3-3 3 3 M17 19.5v-15 M14 16.5l3 3 3-3',
  'arrow-up':   'M12 20V4 M5.5 10.5 12 4l6.5 6.5',
  'arrow-down': 'M12 4v16 M5.5 13.5 12 20l6.5-6.5',
  external:   'M14.5 3.5h6v6 M20.5 3.5L11 13 M18 13.5v6.5h-14v-14h6.5',
  // Document viewer modes: four corners pushed out (full screen), and a
  // frame split in two (back beside the data).
  maximize:   'M3.5 9V3.5H9 M15 3.5h5.5V9 M20.5 15v5.5H15 M9 20.5H3.5V15',
  'side-pane': 'M3.5 4.5h17v15h-17z M10.5 4.5v15',
  // Turn the scanned page a quarter clockwise — an open circle with its
  // arrowhead at the top right.
  'rotate-cw': 'M21.5 5v5.5H16 M19.2 14.5a7.6 7.6 0 1 1-1.8-8l4.1 4',



  // ── status. Colour is applied at the call site and is fixed by meaning:
  // green #15803D (ok), amber #B45309 (warning), red #B91C1C (error).
  check:          'M20 6L9 17l-5-5',
  'check-circle': 'M20.9 11.1V12a8.9 8.9 0 1 1-5.3-8.1 M8.5 11.5l3.2 3.2L20.5 6',
  alert:          'M10.3 4.3L2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z M12 9v4.2 M12 16.8h.01',
  'x-circle':     'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18 M15 9l-6 6 M9 9l6 6',
  info:           'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18 M12 16.5V11.5 M12 8h.01',

  // ── casual accents — a warmer register than the status glyphs above (no
  // wash, no fixed semantic colour): used where a callout is a nudge to
  // finish something, not an incident.
  heart:          'M12 20.3c-4.8-4.1-8.3-7.6-8.3-11A4.7 4.7 0 0 1 12 6.2 4.7 4.7 0 0 1 20.3 9.3c0 3.4-3.5 6.9-8.3 11z',
  // The business's star metric (BE העסק · הכנסות לעובד) — a five-point star.
  star:           'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z',
};

// The status glyphs are the one deliberate exception to the no-fill rule: for
// them the colour IS the message, so a soft wash of that same colour sits
// behind the ink — תקין reads green at a glance, before you've resolved the
// tick. Painted with `currentColor` at low opacity, so a wash automatically
// follows whatever semantic colour the call site sets; nothing to keep in sync.
//
// Two passes per glyph, like two strokes of a loaded brush: a wide, pale one
// and a smaller, denser one offset from it. Neither is concentric with the
// outline — the paint sits a little off the line, the way watercolour does,
// which is what keeps it from reading as a flat vector fill. At 13px it reads
// as a tint; at 40px the two passes give it depth.
const WASH = {
  'check-circle': [
    ['M13.1 2.6a9.4 9.4 0 1 1-2.6 18.6A9.4 9.4 0 0 1 13.1 2.6z', 0.19],
    ['M10.1 6.1a6.4 6.4 0 0 0 3.9 12.2A6.4 6.4 0 0 0 10.1 6.1z', 0.15],
  ],
  alert: [
    ['M11.4 4.6 2.9 19.3a1.6 1.6 0 0 0 1.4 2.4h14.6a1.6 1.6 0 0 0 1.4-2.4L12.6 4.6a.7.7 0 0 0-1.2 0z', 0.19],
    ['M12.5 10 8.4 17.6a1.1 1.1 0 0 0 1 1.7h6.2a1.1 1.1 0 0 0 1-1.7z', 0.15],
  ],
  'x-circle': [
    ['M11.2 2.7a9.4 9.4 0 1 0 2.4 18.6A9.4 9.4 0 0 0 11.2 2.7z', 0.19],
    ['M14.1 6a6.4 6.4 0 0 1-3.8 12.2A6.4 6.4 0 0 1 14.1 6z', 0.15],
  ],
  info: [
    ['M12.9 2.7a9.4 9.4 0 1 1-2.4 18.6A9.4 9.4 0 0 1 12.9 2.7z', 0.19],
    ['M10 6.1a6.4 6.4 0 0 0 4 12.2A6.4 6.4 0 0 0 10 6.1z', 0.15],
  ],
};

export const ICON_NAMES = Object.keys(PATHS);
export const WASHED_ICON_NAMES = Object.keys(WASH);

export default function Icon({ name, size = 18, className = '', strokeWidth = 1.7, flip = false, wash = true, style }) {
  const d = PATHS[name];
  if (!d) return null;
  const washes = wash ? WASH[name] : null;
  return (
    <svg
      className={`peak-ic${className ? ' ' + className : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={flip ? { transform: 'scaleX(-1)', ...style } : style}
    >
      {washes && washes.map(([wd, opacity]) => (
        <path key={wd} d={wd} fill="currentColor" fillOpacity={opacity} stroke="none" />
      ))}
      <path d={d} />
    </svg>
  );
}
